/**
 * Partner Portal Utilities
 * Provides methods to fetch and manage partner organisation data
 */

(function () {
  const DEFAULT_APP_PROXY_SUBPATH_PREFIX = "apps";
  const DEFAULT_APP_PROXY_SUBPATH = "proxy";
  const STORAGE_KEY = "partnerOrganisationInfo";
  const METRICS_STORAGE_KEY = "partnerOrganisationMetrics";

  /**
   * Gets the app proxy endpoint for fetching donation organisations
   * @param {string} prefix - App proxy subpath prefix (default: "apps")
   * @param {string} subpath - App proxy subpath (default: "proxy")
   * @returns {string} The endpoint URL
   */
  const getOrganisationsEndpoint = (prefix, subpath) => {
    const appPrefix = prefix || DEFAULT_APP_PROXY_SUBPATH_PREFIX;
    const appSubpath = subpath || DEFAULT_APP_PROXY_SUBPATH;
    return `/${appPrefix}/${appSubpath}/api/get-donation-organisation-names`;
  };

  /**
   * Gets the app proxy endpoint for fetching organisation metrics
   * @param {string} organisationId - The organisation ID
   * @param {string} prefix - App proxy subpath prefix (default: "apps")
   * @param {string} subpath - App proxy subpath (default: "proxy")
   * @returns {string} The endpoint URL
   */
  const getMetricsEndpoint = (organisationId, prefix, subpath) => {
    const appPrefix = prefix || DEFAULT_APP_PROXY_SUBPATH_PREFIX;
    const appSubpath = subpath || DEFAULT_APP_PROXY_SUBPATH;
    return `/${appPrefix}/${appSubpath}/api/get-donation-organisation-metrics?organisationId=${encodeURIComponent(
      organisationId
    )}`;
  };

  /**
   * Fetches all donation organisations from the API
   * @param {string} prefix - App proxy subpath prefix
   * @param {string} subpath - App proxy subpath
   * @returns {Promise<Array<{name: string, id: string}>>} The donation organisations
   */
  const fetchAllOrganisations = async (prefix, subpath) => {
    const endpoint = getOrganisationsEndpoint(prefix, subpath);

    try {
      const response = await fetch(endpoint, {
        credentials: "same-origin",
      });

      if (!response.ok) {
        throw new Error(`Failed to load donation organisations (${response.status})`);
      }

      const payload = await response.json();
      if (payload && payload.error) {
        throw new Error(payload.error);
      }

      return payload || [];
    } catch (error) {
      console.error("Error fetching organisations:", error);
      return [];
    }
  };

  /**
   * Fetches an organisation by ID and returns its name
   * @param {string} organisationId - The organisation ID to fetch
   * @param {string} prefix - App proxy subpath prefix
   * @param {string} subpath - App proxy subpath
   * @returns {Promise<{name: string, id: string} | null>} The organisation object with name and id, or null if not found
   */
  const fetchOrganisationById = async (organisationId, prefix, subpath) => {
    if (!organisationId || organisationId.trim() === "") {
      return null;
    }

    try {
      const organisations = await fetchAllOrganisations(prefix, subpath);
      const organisation = organisations.find((org) => org.id === organisationId.trim());

      if (organisation) {
        // Store organisation info in sessionStorage for child snippets
        sessionStorage.setItem(
          STORAGE_KEY,
          JSON.stringify({
            id: organisation.id,
            name: organisation.name,
          })
        );
        return organisation;
      }

      // Clear invalid organisation info from sessionStorage
      sessionStorage.removeItem(STORAGE_KEY);
      return null;
    } catch (error) {
      console.error("Error fetching organisation by ID:", error);
      sessionStorage.removeItem(STORAGE_KEY);
      return null;
    }
  };

  /**
   * Gets the stored organisation info from sessionStorage
   * @returns {{name: string, id: string} | null} The organisation info or null
   */
  const getStoredOrganisationInfo = async (organisationId, prefix, subpath) => {
    try {
      let stored = await sessionStorage.getItem(STORAGE_KEY);
      if (stored) {
        return JSON.parse(stored);
      }

      await fetchOrganisationById(organisationId, prefix, subpath);

      stored = await sessionStorage.getItem(STORAGE_KEY);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (error) {
      console.error("Error reading stored organisation info:", error);
    }
    return null;
  };

  /**
   * Gets the stored organisation metrics from sessionStorage. Checks if the data was
   * collected within a given time frame. If the data is out of the time frame, fetches
   * it again from the API.
   *
   * The time frame is set in the function.
   *
   * @param {string} organisationId - The organisation ID
   * @param {string} prefix - App proxy subpath prefix
   * @param {string} subpath - App proxy subpath
   * @returns {Promise<Object | null>} The organisation metrics or null
   */
  const getStoredOrganisationMetrics = async (organisationId, prefix, subpath) => {
    try {
      const stored = sessionStorage.getItem(METRICS_STORAGE_KEY);

      if (stored) {
        const metrics = JSON.parse(stored);
        const fiveMinutesAgo = new Date(Date.now() - 1000 * 60 * 5);
        const isFresh = new Date(metrics.timestamp) > fiveMinutesAgo;

        if (isFresh) {
          return metrics;
        }
      }

      const metrics = await fetchOrganisationMetrics(organisationId, prefix, subpath);

      // Add a timestamp to the response to track when the data was last fetched
      metrics.timestamp = new Date().toISOString();

      // Save for future usage on page reloads
      sessionStorage.setItem(METRICS_STORAGE_KEY, JSON.stringify(metrics));

      return metrics;
    } catch (error) {
      console.error("Error reading stored organisation metrics:", error);
    }

    return null;
  };

  /**
   * Fetches organisation metrics from the API using the organisation ID and prefix plus
   * subpath to construct the endpoint.
   *
   * The data is returned as a JSON object.
   * @param {string} organisationId - The organisation ID
   * @param {string} prefix - App proxy subpath prefix
   * @param {string} subpath - App proxy subpath
   * @returns {Promise<Object>} The organisation metrics
   */
  const fetchOrganisationMetrics = async (organisationId, prefix, subpath) => {
    const endpoint = getMetricsEndpoint(organisationId, prefix, subpath);

    const response = await fetch(endpoint, {
      credentials: "same-origin",
    });

    const responseJson = await response.json();

    if (responseJson.error || !response.status === 200) {
      throw new Error(responseJson.error || "Failed to fetch organisation metrics");
    }

    return responseJson;
  };

  /**
   * Fetches monthly new donors data for an organisation
   * @param {string} organisationId - The organisation ID
   * @param {string} prefix - App proxy subpath prefix
   * @param {string} subpath - App proxy subpath
   * @returns {Promise<{months: Array<{label: string, currentYear: number, previousYear: number}>}>} Monthly donors data
   */
  const fetchMonthlyNewDonors = async (organisationId, prefix, subpath) => {
    return getMonthlyNewDonorsData(organisationId, prefix, subpath);
  };

  /**
   * Extracts the monthly donor data from the stored organisation metrics and returns
   * them to the user.
   *
   * To do this, performs a data munging exercise to transform the data returned from
   * the API into the format required by the chart.
   *
   * @param {string} organisationId - The organisation ID
   * @param {string} prefix - App proxy subpath prefix
   * @param {string} subpath - App proxy subpath
   * @returns {Promise<{years: Array<{year: number, months: Array<{label: string, currentYear: number, previousYear: number}>}>}>} Monthly donors data
   */
  const getMonthlyNewDonorsData = async (organisationId, prefix, subpath) => {
    const metrics = await getStoredOrganisationMetrics(organisationId, prefix, subpath);
    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

    if (!metrics) return { years: [] };

    const months = metrics.monthlyDonorData || [];

    const allYears = months.map((month) => month.year);
    const uniqueYears = [...new Set(allYears)];

    // Create a data object to power the chart structure
    const years = [];

    // Set up the empty version of the data object for each existing year.
    for (const year of uniqueYears) {
      const yearData = { year: year, months: [] };

      for (const month of monthNames) {
        yearData.months.push({
          label: `${month} ${year}`,
          currentYear: 0,
          previousYear: 0,
        });
      }

      years.push(yearData);
    }

    // Find the correct month in the year by label and update the current year and
    // the year ahead's previous year.
    for (const month of months) {
      const yearData = years.find((y) => y.year === month.year);
      const yearAheadData = years.find((y) => y.year === month.year + 1);

      const monthShort = month.month.slice(0, 3);

      const monthData = yearData.months.find((m) => m.label.slice(0, 3) === monthShort);
      monthData.currentYear = month.newDonorsCount;

      if (!yearAheadData) {
        continue;
      }

      const monthAheadData = yearAheadData.months.find((m) => m.label.slice(0, 3) === monthShort);
      monthAheadData.previousYear = monthData.currentYear;
    }

    return { years: years };
  };

  /**
   * Returns mock monthly new donors data
   * @returns {{years: Array<{year: number, months: Array<{label: string, currentYear: number, previousYear: number}>}>}} Mock data
   */
  const getMockMonthlyDonorsData = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

    // Generate data for last 5 years
    for (let yearOffset = 0; yearOffset < 5; yearOffset++) {
      const year = currentYear - yearOffset;
      const months = [];

      for (let monthIndex = 0; monthIndex < 12; monthIndex++) {
        const monthName = monthNames[monthIndex];
        const previousYearValue = yearOffset > 0 ? Math.floor(Math.random() * 100) + 80 : 0;

        // Mock data: values between 100-200 for current year, 80-180 for previous year
        const currentYearValue = Math.floor(Math.random() * 100) + 100;

        months.push({
          label: `${monthName} ${year}`,
          currentYear: currentYearValue,
          previousYear: previousYearValue,
        });
      }

      years.push({
        year: year,
        months: months,
      });
    }

    return { years };
  };

  // Expose methods globally
  window.PartnerPortalUtils = {
    fetchAllOrganisations,
    fetchOrganisationById,
    getStoredOrganisationInfo,
    getStoredOrganisationMetrics,
    fetchMonthlyNewDonors,
  };
})();
